import { User } from 'oidc-client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExaminerFunction, ExaminerSampleAssessmentGroupFunction } from '../pages/Assessment/Assessment.types';
import jwt_decode from 'jwt-decode';
import EnvWindow from '../utils/env';
import { useShowLoader } from '../Context/Layout.context';
import { getUserAccountTypeId, loadUserFromStorage } from '../services/users/identity/user.service';
import { useDispatch } from 'react-redux';
import { AccountTypeEnum } from '../models/user.types';

export interface AssessmentSessionTokenInfo {
  account_type: 'examiner' | 'administrator' | 'examcommitteeemployee';
  role: string;
  function: ExaminerFunction;
  assessmentSessionId: string;
  domesticCoordinatorId: string;
  examTaskGroupCoordinator: string;
  examiningTeamChairmanId: string;
  examiningTeamId: string;
  sampleAssessmentGroupFunction: ExaminerSampleAssessmentGroupFunction | null;
  examinerPermissionId: string;
  examTaskGroupId: string;
  isChairman?: IsChairman;
  isVerifier?: boolean;
}

export enum IsChairman {
  No,
  Yes,
}

export function retrieveUserFromStorage(): User {
  const sessionStorageUserInfo = sessionStorage.getItem(`oidc.user:${EnvWindow.REACT_APP_AUTHORITY_URL}:ckeone_react`);

  return sessionStorageUserInfo ? JSON.parse(sessionStorageUserInfo) : undefined;
}

export const isUserLoggedIn = () => {
  try {
    const userInfo = retrieveUserFromStorage() || undefined;

    if (userInfo === undefined || userInfo === null) return false;

    return true;
  } catch (e) {
    console.error('Error while checking if user is logged in', e);

    return false;
  }
};

export const getIsUserKK = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.function === ExaminerFunction.KK;

export const getIsUserKGZ = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.function === ExaminerFunction.KGZ;

export const getIsUserPZE = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.function === ExaminerFunction.PZE;

export const getIsUserCZE = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.function === ExaminerFunction.CZE;

export const getIsUserCZWO = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.function === ExaminerFunction.CZWO ||
  userAssessmentSessionInfo.sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.CZWO;

export const getIsUserPZWO = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.function === ExaminerFunction.PZWO ||
  userAssessmentSessionInfo.sampleAssessmentGroupFunction === ExaminerSampleAssessmentGroupFunction.PZWO;

export const getIsUserVerifier = (userAssessmentSessionInfo: AssessmentSessionTokenInfo) =>
  userAssessmentSessionInfo.isVerifier;

export const getIsUserTechAdmin = (user: User) =>
  user && getUserAccountTypeId(user) === AccountTypeEnum.ExamCommitteeEmployee;

function useSessionUser() {
  const auth = retrieveUserFromStorage();
  const showLoader = useShowLoader();
  const dispatch = useDispatch();
  const [user, setUser] = useState<User>(auth);
  const [userAssessmentSessionInfo, setUserAssessmentSessionInfo] = useState<AssessmentSessionTokenInfo>({} as never);

  useEffect(() => {
    if (auth) {
      setUser(auth);

      const decoded: AssessmentSessionTokenInfo = jwt_decode(auth.access_token);

      if (decoded) {
        setUserAssessmentSessionInfo(decoded);
      }
    }
  }, [auth ? auth.access_token : undefined]);

  const reloadAssessmentSession = useCallback(
    async (assessmentSessionId: string, asExaminer?: boolean) => {
      showLoader(true, 'Trwa przeładowanie sesji egzaminacyjnej');

      await loadUserFromStorage(dispatch, assessmentSessionId, setUser, asExaminer).then(() => {
        showLoader(false);
      });
    },
    [dispatch, showLoader],
  );

  return useMemo(
    () => ({
      user,
      setUser,
      userAssessmentSessionInfo,
      isSessionLoaded: !!Object.keys(userAssessmentSessionInfo).length,
      reloadAssessmentSession,
      isUserKK: getIsUserKK(userAssessmentSessionInfo),
      isUserKGZ: getIsUserKGZ(userAssessmentSessionInfo),
      isUserPZE: getIsUserPZE(userAssessmentSessionInfo),
      isUserCZE: getIsUserCZE(userAssessmentSessionInfo),
      isUserCZWO: getIsUserCZWO(userAssessmentSessionInfo),
      isUserPZWO: getIsUserPZWO(userAssessmentSessionInfo),
      isVerifier: getIsUserVerifier(userAssessmentSessionInfo),
      isTechAdmin: getIsUserTechAdmin(user),
    }),
    [reloadAssessmentSession, user, userAssessmentSessionInfo],
  );
}

export default useSessionUser;
